import {createTheme, CssBaseline, ThemeProvider} from '@mui/material';
import {SnackbarProvider} from 'notistack';
import {useEffect, useMemo} from 'react';
import {Route, Routes, useLocation, useNavigate} from 'react-router-dom';

import {BackendMessages} from './components/backend/Messages';
import {BackendPolling} from './components/backend/Polling';
import {LicenseCheck} from './components/license/LicenseCheck';
import {useAppSelector} from './hooks/redux';
import {AdminCompanySettingsPage} from './pages/admin/AdminCompanySettingsPage';
import {AdminGeoFencePage} from './pages/admin/AdminGeoFencePage';
import {AdminHelpPage} from './pages/admin/AdminHelpPage';
import {AdminProductsPage} from './pages/admin/AdminProductsPage';
import AdminProximityImportListPage from './pages/admin/AdminProximityImportListPage';
import AdminUserGroupListPage from './pages/admin/AdminUserGroupListPage';
import AdminUserListPage from './pages/admin/AdminUserListPage';
import {LicenseCheckPage} from './pages/auth/LicenseCheckPage';
import {PasswordResetPage} from './pages/auth/PasswordResetPage';
import {PasswordResetRequestPage} from './pages/auth/PasswordResetRequestPage';
import {SigninPage} from './pages/auth/SigninPage';
import DashboardItemPage from './pages/dashboards/item';
import DashboardListPage from './pages/dashboards/list';
import {IndicatorPanelContextProvider} from './pages/indicator-panel/contexts';
import {IndicatorPanel} from './pages/indicator-panel/IndicatorPanel';
import {IndicatorWidgetEditor} from './pages/indicator-panel/IndicatorWidgetEditor';
import DashboardPanelItemPage from './pages/panels/item';
import DashboardPanelListPage from './pages/panels/list';
import ReportRenderPage from './pages/report-render/ReportRenderPage';
import SubscriptionListPage from './pages/subscriptions/list';
import reduxSelectors from './redux/selectors';

export const App = () => {
  const isDarkMode = useAppSelector(reduxSelectors.app.getIsDarkMode);
  const isCompactMode = useAppSelector(reduxSelectors.app.getIsCompactMode);

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          h2: {
            fontSize: isCompactMode ? 20 : 40,
            fontWeight: isCompactMode ? 400 : 600,
          },
          body2: {},
        },
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: {
            main: isDarkMode ? '#90caf9' : '#0c2646',
          },
          secondary: {
            main: '#666666',
          },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: `
              .simplebar-vertical .simplebar-scrollbar::before {
                content: '';
                display: block;
                width: 6px;
                margin-left: 17px;
                background: #999;
                border-radius: 4px;
                opacity: 1 !important;
              }
              .simplebar-track.simplebar-vertical {
                width: 25px !important;
                right: 0;
                opacity: 1 !important;
                visibility: visible !important;
                pointer-events: auto;
                cursor: pointer;
              }
              .simplebar-track.simplebar-vertical:hover {
                cursor: pointer;
              }
              .simplebar-track.simplebar-horizontal {
                height: 13px !important;
                bottom: 0;
                opacity: 1 !important;
                visibility: visible !important;
                pointer-events: auto;
                cursor: pointer;
              }
              .simplebar-scrollbar {
                opacity: 1 !important;
                visibility: visible !important;
                cursor: grab;
              }
              .simplebar-scrollbar:active {
                cursor: grabbing;
              }
              .simplebar-horizontal .simplebar-scrollbar::before {
                content: '';
                display: block;
                height: 6px;
                margin-top: 5px;
                background: #999;
                border-radius: 4px;
                opacity: 1 !important;
              }
              .simplebar-scrollbar:hover::before {
                background: #666;
              }
            `,
          },
          MuiBackdrop: {
            styleOverrides: {
              root: {
                zIndex: 3,
                backgroundColor: isDarkMode ? '#44444490' : '#00000080',
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                '&.MuiInputLabel-shrink': {
                  fontWeight: 700,
                },
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              root: {
                backgroundColor: isDarkMode ? '#44444490' : '#00000080',
              },
              paper: ({theme}) => ({
                border: '1px solid #9b9b9b',
                backgroundColor: theme.palette.background.paper,
                backgroundImage: 'none',
              }),
            },
          },
          MuiToolbar: {
            defaultProps: {
              variant: isCompactMode ? 'dense' : 'regular',
            },
          },
          MuiTable: {
            defaultProps: {
              size: isCompactMode ? 'small' : 'medium',
            },
            styleOverrides: {
              root: {
                '&.MuiTable-stickyHeader': {
                  thead: {
                    th: {
                      '&.MuiTableCell-stickyHeader': {
                        top: -1,
                      },
                      '&.MuiTableCell-paddingCheckbox': {
                        width: 60,
                      },
                      p: {
                        marginTop: isCompactMode ? 0 : 10,
                        marginBottom: isCompactMode ? 0 : 10,
                      },
                      svg: {
                        height: isCompactMode ? 16 : 24,
                        width: isCompactMode ? 16 : 24,
                      },
                    },
                  },
                  tbody: {
                    tr: {
                      td: {
                        '&.MuiTableCell-paddingCheckbox': {
                          width: 60,
                        },
                        p: {
                          marginTop: isCompactMode ? 0 : 10,
                          marginBottom: isCompactMode ? 0 : 10,
                        },
                        svg: {
                          height: isCompactMode ? 16 : 24,
                          fontSize: isCompactMode ? 16 : 24,
                        },
                      },
                    },
                  },
                },
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              paddingCheckbox: {
                width: 'auto',
              },
            },
          },
        },
      }),
    [isDarkMode, isCompactMode]
  );

  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/dashboards/primary');
    }
  }, [location.pathname]);

  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{vertical: 'top', horizontal: 'center'}}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BackendMessages />
        <BackendPolling />
        <LicenseCheck />

        <Routes>
          {/* Headless-browser print route — no app chrome, render token acts as auth */}
          <Route path="/report-render" element={<ReportRenderPage />} />

          <Route path="/auth/signin" element={<SigninPage />} />
          <Route
            path="/auth/password-reset"
            element={<PasswordResetRequestPage />}
          />
          <Route
            path="/auth/password-reset/:token"
            element={<PasswordResetPage />}
          />
          <Route path="/dashboards" element={<DashboardListPage />} />
          <Route path="/dashboards/:id" element={<DashboardItemPage />} />
          <Route path="/panels" element={<DashboardPanelListPage />} />
          <Route path="/panels/:code" element={<DashboardPanelItemPage />} />
          <Route path="/indicator" element={<IndicatorPanelContextProvider />}>
            <Route path="panel" element={<IndicatorPanel />} />
            <Route path="widget" element={<IndicatorWidgetEditor />} />
          </Route>
          <Route path="/subscriptions" element={<SubscriptionListPage />} />
          <Route path="/admin/products" element={<AdminProductsPage />} />
          <Route path="/admin/users" element={<AdminUserListPage />} />
          <Route
            path="/admin/user-groups"
            element={<AdminUserGroupListPage />}
          />
          <Route
            path="/admin/company-settings"
            element={<AdminCompanySettingsPage />}
          />
          <Route
            path="/admin/proximity-import"
            element={<AdminProximityImportListPage />}
          />
          <Route path="/admin/geo-fence" element={<AdminGeoFencePage />} />
          <Route path="/admin/help" element={<AdminHelpPage />} />
          <Route path="/license-check" element={<LicenseCheckPage />} />
        </Routes>
      </ThemeProvider>
    </SnackbarProvider>
  );
};
